import React, { useState } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, Flag } from 'lucide-react';

const CATEGORIES = [
  { value: 'no_show', label: 'No-show' },
  { value: 'left_early', label: 'Left booking early' },
  { value: 'late', label: 'Arrived late' },
  { value: 'unprofessional', label: 'Unprofessional / disrespectful' },
  { value: 'dress_code', label: 'Dress code / preparation' },
  { value: 'solicitation', label: 'Solicited tips / food / money / favors' },
  { value: 'off_platform', label: 'Took client off-platform' },
  { value: 'incomplete_media', label: 'Incomplete photos / recap / deliverables' },
  { value: 'abandonment', label: 'Abandoned booking / improper release' },
  // Severe conduct breaches (auto-set severity to Severe)
  { value: 'theft', label: 'Theft / taking or damaging property', severe: true },
  { value: 'violence', label: 'Violence / threats / fighting', severe: true },
  { value: 'harassment', label: 'Harassment / intimidation', severe: true },
  { value: 'impaired', label: 'Working while impaired / intoxicated', severe: true },
  { value: 'drugs', label: 'Illegal drug activity', severe: true },
  { value: 'falsified_records', label: 'Falsified attendance / GPS / records', severe: true },
  { value: 'confidentiality', label: 'Breach of confidential information', severe: true },
  { value: 'safety', label: 'Safety / serious incident not reported', severe: true },
  { value: 'other', label: 'Other' },
];

const SEVERE_CATEGORIES = new Set(CATEGORIES.filter((c) => c.severe).map((c) => c.value));

/**
 * FlagViolationModal — admin flags a Code of Conduct breach against a
 * talent for a specific booking. Surfaces on the talent's reliability record.
 */
export const FlagViolationModal = ({ open, onClose, talentId, talentName, bookingId, shiftId, eventLabel, clientName, onFlagged }) => {
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('minor');
  const [description, setDescription] = useState('');
  const [clientComplaint, setClientComplaint] = useState('');
  const [blockClient, setBlockClient] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!category) { toast.error('Select a violation category'); return; }
    setBusy(true);
    try {
      const res = await api.post('/admin/coc-violations', {
        talent_id: talentId,
        booking_id: bookingId || null,
        shift_id: shiftId || null,
        category,
        severity,
        description: description.trim() || null,
        client_complaint: clientComplaint.trim() || null,
        block_client: blockClient,
      });
      toast.success(res.data?.client_blocked
        ? `Breach flagged — talent blocked from ${clientName || 'this client'}`
        : 'Code of Conduct breach flagged');
      setCategory(''); setSeverity('minor'); setDescription(''); setClientComplaint(''); setBlockClient(true);
      onFlagged?.();
      onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not flag violation'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose?.(); }}>
      <DialogContent data-testid="flag-violation-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-red-600" /> Flag Code of Conduct Breach
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {talentName && <p className="text-sm text-slate-600">Talent: <span className="font-semibold">{talentName}</span></p>}
          {eventLabel && <p className="text-xs text-slate-500 -mt-2">Event: <span className="font-medium text-slate-700">{eventLabel}</span></p>}
          <div>
            <label className="text-xs font-semibold text-slate-600">Category</label>
            <Select value={category} onValueChange={(v) => { setCategory(v); if (SEVERE_CATEGORIES.has(v)) setSeverity('severe'); }}>
              <SelectTrigger data-testid="violation-category"><SelectValue placeholder="Select a category" /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Severity</label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger data-testid="violation-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="minor">Minor</SelectItem>
                <SelectItem value="major">Major</SelectItem>
                <SelectItem value="severe">Severe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Client complaint / reason (optional)</label>
            <Textarea rows={2} value={clientComplaint} onChange={(e) => setClientComplaint(e.target.value)} placeholder="What did the client report?" data-testid="violation-client-complaint" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Internal notes (optional)</label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Any additional detail" data-testid="violation-description" />
          </div>
          <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-red-200 bg-red-50 p-2.5" data-testid="violation-block-label">
            <input type="checkbox" checked={blockClient} onChange={(e) => setBlockClient(e.target.checked)} className="mt-0.5 h-4 w-4 accent-red-600" data-testid="violation-block-client" />
            <span className="text-xs text-slate-700">
              Block this talent from {clientName || 'this client'} — they won't see or be bookable for this client's gigs. An admin can lift this later.
            </span>
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onClose?.()} disabled={busy}>Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={submit} disabled={busy} data-testid="violation-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Flag Breach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FlagViolationModal;
